import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { supabase } from '../../lib/supabaseClient';
import Spinner from '../Atoms/Spinner';

// ─── Styled Components ───────────────────────────────────────
const PageContainer = styled.div`
    max-width: 720px;
    margin: 0 auto;
    padding: 40px 20px 80px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const LogoImg = styled.img`
    display: block;
    margin: 0 auto 20px;
    max-width: 180px;
`;

const PageTitle = styled.h1`
    text-align: center;
    font-size: 1.6rem;
    color: #1a1a2e;
    margin-bottom: 8px;
    font-weight: 700;
`;

const PageSubtitle = styled.p`
    text-align: center;
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 32px;
`;

const DesignerBadge = styled.div`
    text-align: center;
    font-size: 1.1rem;
    font-weight: 600;
    color: rgb(24, 51, 189);
    background: #f0f4ff;
    border: 1px solid #d0d8ff;
    border-radius: 8px;
    padding: 12px 20px;
    margin-bottom: 28px;
`;

const SectionCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 24px;
    margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
    font-size: 1.1rem;
    color: rgb(24, 51, 189);
    margin-bottom: 20px;
    font-weight: 700;
    border-bottom: 2px solid rgb(24, 51, 189);
    padding-bottom: 8px;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;

    label {
        font-size: 13px;
        font-weight: 600;
        color: #555;
        margin-bottom: 4px;
    }

    &.required label::after {
        content: ' *';
        color: #c0392b;
    }

    input, textarea, select {
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        transition: border-color 0.2s;
        background: white;

        &:focus {
            outline: none;
            border-color: rgb(24, 51, 189);
        }
    }

    textarea {
        resize: vertical;
        min-height: 100px;
    }
`;

const FieldRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 500px) {
        grid-template-columns: 1fr;
    }
`;

const DropZone = styled.div`
    border: 2px dashed ${props => props.isDragOver ? 'rgb(24, 51, 189)' : '#ccc'};
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    color: #888;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    background: ${props => props.isDragOver ? '#f0f4ff' : '#fafafa'};
    margin-bottom: 12px;

    &:hover {
        border-color: rgb(24, 51, 189);
        background: #f0f4ff;
    }
`;

const SubmitBtn = styled.button`
    width: 100%;
    padding: 14px 24px;
    background: rgb(24, 51, 189);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 10px;

    &:hover:not(:disabled) {
        background: #1a237e;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Alert = styled.div`
    padding: 16px 20px;
    border-radius: 8px;
    font-size: 15px;
    margin-bottom: 20px;

    ${props => props.variant === 'success' && `
        background-color: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
    `}

    ${props => props.variant === 'danger' && `
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
    `}

    ${props => props.variant === 'info' && `
        background-color: #e8f0fe;
        color: #1a3b6b;
        border: 1px solid #d0ddf0;
    `}
`;

const ExistingPhotoCard = styled.div`
    display: flex;
    gap: 14px;
    padding: 14px;
    border: 1px solid ${props => props.deleted ? '#f5c6cb' : '#e0e0e0'};
    border-radius: 8px;
    margin-bottom: 12px;
    background: ${props => props.deleted ? '#fff5f5' : '#fafafa'};
    opacity: ${props => props.deleted ? 0.6 : 1};
    transition: all 0.2s;

    @media (max-width: 500px) {
        flex-direction: column;
        align-items: center;
    }
`;

const PhotoThumb = styled.div`
    width: 120px;
    height: 120px;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ReplacementOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(24, 51, 189, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        background: rgb(24, 51, 189);
        color: white;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
    }
`;

const PhotoDetails = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 8px;
    min-width: 0;
`;

const PhotoActionRow = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const SmallBtn = styled.button`
    padding: 6px 12px;
    border: 1px solid ${props => props.variant === 'danger' ? '#dc3545' : props.variant === 'restore' ? '#28a745' : '#ddd'};
    background: ${props => props.variant === 'danger' ? '#ffeaea' : props.variant === 'restore' ? '#e6f9ed' : 'white'};
    color: ${props => props.variant === 'danger' ? '#dc3545' : props.variant === 'restore' ? '#28a745' : '#333'};
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
        opacity: 0.8;
    }
`;

const NewPhotoCard = styled.div`
    display: flex;
    gap: 14px;
    padding: 14px;
    border: 2px solid rgb(24, 51, 189);
    border-radius: 8px;
    margin-bottom: 12px;
    background: #f0f4ff;

    @media (max-width: 500px) {
        flex-direction: column;
        align-items: center;
    }
`;

const NewPhotoThumb = styled.div`
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const CategorySelect = styled.select`
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    background: white;

    &:focus {
        outline: none;
        border-color: rgb(24, 51, 189);
    }
`;

const RemoveBtn = styled.button`
    background: #c0392b;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    flex-shrink: 0;
    align-self: flex-start;
`;

// ─── Component ───────────────────────────────────────────────
const FD100ExpoConfirmationForm = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const { i18n } = useTranslation();
    const isFr = i18n.language !== 'en';

    // Loading / UI states
    const [loading, setLoading] = useState(true);
    const [piece, setPiece] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Form fields
    const [nomPiece, setNomPiece] = useState('');
    const [designerNames, setDesignerNames] = useState('');
    const [fabricant, setFabricant] = useState('');
    const [typologie, setTypologie] = useState('');
    const [dimensions, setDimensions] = useState('');
    const [materiaux, setMateriaux] = useState('');
    const [poids, setPoids] = useState('');
    const [valeurAssurance, setValeurAssurance] = useState('');
    const [description, setDescription] = useState('');
    const [contactPrenom, setContactPrenom] = useState('');
    const [contactNom, setContactNom] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactTelephone, setContactTelephone] = useState('');

    // Photos: { url, credit, category, deleted, replacementFile }
    const [existingPhotos, setExistingPhotos] = useState([]);
    // New photos: { file, credit, category }
    const [newPhotos, setNewPhotos] = useState([]);
    const [dragOver, setDragOver] = useState(false);

    const newPhotoInputRef = useRef(null);
    const replaceInputRef = useRef(null);
    const [replacingIndex, setReplacingIndex] = useState(null);

    // ─── Load data ───────────────────────────────────────────
    useEffect(() => {
        if (!token) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        const loadData = async () => {
            try {
                const { data, error } = await supabase
                    .from('fd100_expo_finale_pieces')
                    .select('*')
                    .eq('token', token)
                    .single();

                if (error || !data) {
                    setNotFound(true);
                    setLoading(false);
                    return;
                }

                setPiece(data);
                setNomPiece(data.nom_piece || '');
                setDesignerNames(data.designer_names || '');
                setFabricant(data.fabricant || '');
                setTypologie(data.typologie || '');
                setDimensions(data.dimensions || '');
                setMateriaux(data.materiaux || '');
                setPoids(data.poids || '');
                setValeurAssurance(data.valeur_assurance || '');
                setDescription(data.description || '');
                setContactPrenom(data.contact_prenom || '');
                setContactNom(data.contact_nom || '');
                setContactEmail(data.contact_email || '');
                setContactTelephone(data.contact_telephone || '');

                // Build existing photos from the photos JSONB
                const photos = (data.photos || []).map(p => ({
                    url: p.url,
                    credit: p.credit || '',
                    category: p.category || '',
                    deleted: false,
                    replacementFile: null,
                }));
                setExistingPhotos(photos);

                setLoading(false);
            } catch (err) {
                console.error('Error loading data:', err);
                setNotFound(true);
                setLoading(false);
            }
        };

        loadData();
    }, [token]);

    // ─── Existing photo handlers ─────────────────────────────
    const updateExistingCredit = useCallback((index, credit) => {
        setExistingPhotos(prev => prev.map((p, i) => i === index ? { ...p, credit } : p));
    }, []);

    const updateExistingCategory = useCallback((index, category) => {
        setExistingPhotos(prev => prev.map((p, i) => i === index ? { ...p, category } : p));
    }, []);

    const deleteExistingPhoto = useCallback((index) => {
        setExistingPhotos(prev => prev.map((p, i) => i === index ? { ...p, deleted: true } : p));
    }, []);

    const restoreExistingPhoto = useCallback((index) => {
        setExistingPhotos(prev => prev.map((p, i) => i === index ? { ...p, deleted: false } : p));
    }, []);

    const handleReplacePhoto = useCallback((index) => {
        setReplacingIndex(index);
        replaceInputRef.current?.click();
    }, []);

    const onReplacementSelected = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file && replacingIndex !== null) {
            setExistingPhotos(prev => prev.map((p, i) =>
                i === replacingIndex ? { ...p, replacementFile: file } : p
            ));
        }
        setReplacingIndex(null);
        e.target.value = '';
    }, [replacingIndex]);

    const cancelReplacement = useCallback((index) => {
        setExistingPhotos(prev => prev.map((p, i) =>
            i === index ? { ...p, replacementFile: null } : p
        ));
    }, []);

    // ─── New photo handlers ──────────────────────────────────
    const handleNewPhotoDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer?.files || e.target?.files || [])
            .filter(f => f.type.startsWith('image/'));
        if (files.length) {
            setNewPhotos(prev => [...prev, ...files.map(file => ({
                file, credit: '', category: 'packshot',
            }))]);
        }
    }, []);

    const updateNewCredit = useCallback((index, credit) => {
        setNewPhotos(prev => prev.map((p, i) => i === index ? { ...p, credit } : p));
    }, []);

    const updateNewCategory = useCallback((index, category) => {
        setNewPhotos(prev => prev.map((p, i) => i === index ? { ...p, category } : p));
    }, []);

    const removeNewPhoto = useCallback((index) => {
        setNewPhotos(prev => prev.filter((_, i) => i !== index));
    }, []);

    // ─── Submit ──────────────────────────────────────────────
    const handleSubmit = async () => {
        setSubmitError('');

        // Validate all fields are filled
        const requiredFields = [
            { value: nomPiece, label: isFr ? 'Nom de la pièce' : 'Piece name' },
            { value: designerNames, label: 'Designer' },
            { value: fabricant, label: isFr ? 'Fabricant / Éditeur' : 'Manufacturer / Publisher' },
            { value: typologie, label: isFr ? 'Typologie' : 'Typology' },
            { value: dimensions, label: 'Dimensions' },
            { value: materiaux, label: isFr ? 'Matériaux' : 'Materials' },
            { value: poids, label: isFr ? 'Poids' : 'Weight' },
            { value: valeurAssurance, label: isFr ? 'Valeur d\'assurance' : 'Insurance value' },
            { value: description, label: 'Description' },
            { value: contactPrenom, label: isFr ? 'Prénom' : 'First name' },
            { value: contactNom, label: isFr ? 'Nom' : 'Last name' },
            { value: contactEmail, label: 'Email' },
            { value: contactTelephone, label: isFr ? 'Téléphone' : 'Phone' },
        ];
        const missing = requiredFields.filter(f => !(f.value || '').toString().trim());
        if (missing.length > 0) {
            setSubmitError(
                (isFr ? 'Veuillez remplir tous les champs obligatoires : ' : 'Please fill in all required fields: ')
                + missing.map(f => f.label).join(', ')
            );
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setSubmitting(true);

        try {
            const finalPhotos = [];

            // Process existing photos (non-deleted)
            for (const photo of existingPhotos) {
                if (photo.deleted) continue;

                if (photo.replacementFile) {
                    const ext = photo.replacementFile.name.split('.').pop().toLowerCase();
                    const fileName = `finale/${token}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
                    const { error: upErr } = await supabase.storage
                        .from('fd100-exposition-photos')
                        .upload(fileName, photo.replacementFile);
                    if (upErr) { console.error('Upload error:', upErr); continue; }
                    const { data: { publicUrl } } = supabase.storage
                        .from('fd100-exposition-photos')
                        .getPublicUrl(fileName);

                    finalPhotos.push({
                        url: publicUrl,
                        credit: photo.credit,
                        category: photo.category,
                    });
                } else {
                    finalPhotos.push({
                        url: photo.url,
                        credit: photo.credit,
                        category: photo.category,
                    });
                }
            }

            // Upload new photos
            for (const np of newPhotos) {
                const ext = np.file.name.split('.').pop().toLowerCase();
                const fileName = `finale/${token}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
                const { error: upErr } = await supabase.storage
                    .from('fd100-exposition-photos')
                    .upload(fileName, np.file);
                if (upErr) { console.error('Upload error:', upErr); continue; }
                const { data: { publicUrl } } = supabase.storage
                    .from('fd100-exposition-photos')
                    .getPublicUrl(fileName);

                finalPhotos.push({
                    url: publicUrl,
                    credit: np.credit,
                    category: np.category,
                });
            }

            const { error } = await supabase
                .from('fd100_expo_finale_pieces')
                .update({
                    nom_piece: nomPiece,
                    designer_names: designerNames,
                    fabricant,
                    typologie,
                    dimensions,
                    materiaux,
                    poids,
                    valeur_assurance: valeurAssurance,
                    description,
                    photos: finalPhotos,
                    contact_prenom: contactPrenom,
                    contact_nom: contactNom,
                    contact_email: contactEmail,
                    contact_telephone: contactTelephone,
                    submitted_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                })
                .eq('id', piece.id);

            if (error) throw error;

            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            console.error('Submit error:', err);
            setSubmitError(isFr
                ? 'Une erreur est survenue. Veuillez réessayer.'
                : 'An error occurred. Please try again.');
        }
        setSubmitting(false);
    };

    // ─── Render ──────────────────────────────────────────────
    if (loading) {
        return (
            <PageContainer>
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spinner />
                </div>
            </PageContainer>
        );
    }

    if (notFound || !token) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <Alert variant="danger">
                    {isFr
                        ? 'Lien invalide ou pièce introuvable.'
                        : 'Invalid link or piece not found.'}
                </Alert>
            </PageContainer>
        );
    }

    if (submitted) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <Alert variant="success" style={{ textAlign: 'center', fontSize: '16px' }}>
                    {isFr
                        ? 'Merci ! Vos informations ont bien été enregistrées. Nous reviendrons vers vous prochainement.'
                        : 'Thank you! Your information has been saved. We will get back to you shortly.'}
                </Alert>
            </PageContainer>
        );
    }

    const activeExisting = existingPhotos.filter(p => !p.deleted);
    const deletedExisting = existingPhotos.filter(p => p.deleted);

    return (
        <PageContainer>
            <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
            <PageTitle>
                {isFr
                    ? 'Exposition « Hospitalité lyrique » — Confirmation'
                    : 'Exhibition "Hospitalité lyrique" — Confirmation'}
            </PageTitle>
            <PageSubtitle>
                {isFr
                    ? 'Galerie Le FRENCH DESIGN — 120 avenue Ledru Rollin, 75011 Paris'
                    : 'Le FRENCH DESIGN Gallery — 120 avenue Ledru Rollin, 75011 Paris'}
            </PageSubtitle>

            {(designerNames || nomPiece) && (
                <DesignerBadge>
                    {designerNames}{nomPiece ? ` — ${nomPiece}` : ''}
                </DesignerBadge>
            )}

            {piece?.submitted_at && (
                <Alert variant="info">
                    {isFr
                        ? 'Vous avez déjà soumis ce formulaire. Vous pouvez modifier vos réponses ci-dessous.'
                        : 'You have already submitted this form. You can update your answers below.'}
                </Alert>
            )}

            {submitError && <Alert variant="danger">{submitError}</Alert>}

            {/* ── Section 1: Piece Information ── */}
            <SectionCard>
                <SectionTitle>
                    {isFr ? 'Informations sur la pièce' : 'Piece information'}
                </SectionTitle>

                <FormGroup className="required">
                    <label>{isFr ? 'Nom de la pièce' : 'Piece name'}</label>
                    <input value={nomPiece} onChange={e => setNomPiece(e.target.value)} />
                </FormGroup>

                <FieldRow>
                    <FormGroup className="required">
                        <label>{isFr ? 'Designer' : 'Designer'}</label>
                        <input value={designerNames} onChange={e => setDesignerNames(e.target.value)} />
                    </FormGroup>
                    <FormGroup className="required">
                        <label>{isFr ? 'Fabricant / Éditeur' : 'Manufacturer / Publisher'}</label>
                        <input value={fabricant} onChange={e => setFabricant(e.target.value)} />
                    </FormGroup>
                </FieldRow>

                <FormGroup className="required">
                    <label>{isFr ? 'Typologie' : 'Typology'}</label>
                    <input
                        value={typologie}
                        onChange={e => setTypologie(e.target.value)}
                        placeholder={isFr ? 'Ex: Luminaire, Assise, Table...' : 'Ex: Lighting, Seating, Table...'}
                    />
                </FormGroup>

                <FieldRow>
                    <FormGroup className="required">
                        <label>{isFr ? 'Dimensions' : 'Dimensions'}</label>
                        <input
                            value={dimensions}
                            onChange={e => setDimensions(e.target.value)}
                            placeholder={isFr ? 'L x l x H (cm)' : 'W x D x H (cm)'}
                        />
                    </FormGroup>
                    <FormGroup className="required">
                        <label>{isFr ? 'Poids' : 'Weight'}</label>
                        <input
                            value={poids}
                            onChange={e => setPoids(e.target.value)}
                            placeholder={isFr ? 'En kg' : 'In kg'}
                        />
                    </FormGroup>
                </FieldRow>

                <FormGroup className="required">
                    <label>{isFr ? 'Matériaux' : 'Materials'}</label>
                    <input
                        value={materiaux}
                        onChange={e => setMateriaux(e.target.value)}
                        placeholder={isFr ? 'Ex: Bois, Métal, Verre...' : 'Ex: Wood, Metal, Glass...'}
                    />
                </FormGroup>

                <FormGroup className="required">
                    <label>{isFr ? 'Valeur d\'assurance (€)' : 'Insurance value (€)'}</label>
                    <input
                        value={valeurAssurance}
                        onChange={e => setValeurAssurance(e.target.value)}
                        placeholder={isFr ? 'Montant en euros' : 'Amount in euros'}
                    />
                </FormGroup>

                <FormGroup className="required">
                    <label>{isFr ? 'Description de la pièce' : 'Piece description'}</label>
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder={isFr
                            ? 'Décrivez votre pièce, son histoire, son inspiration...'
                            : 'Describe your piece, its story, its inspiration...'}
                    />
                </FormGroup>
            </SectionCard>

            {/* ── Section 2: Photos ── */}
            <SectionCard>
                <SectionTitle>
                    {isFr ? 'Photos' : 'Photos'}
                </SectionTitle>
                <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
                    {isFr
                        ? 'Ajoutez les crédits photographe, le type de photo, remplacez ou supprimez les photos existantes, ou ajoutez-en de nouvelles.'
                        : 'Add photographer credits, photo type, replace or delete existing photos, or add new ones.'}
                </p>

                {/* Active existing photos */}
                {activeExisting.map((photo) => {
                    const realIndex = existingPhotos.indexOf(photo);
                    return (
                        <ExistingPhotoCard key={photo.url + realIndex} deleted={false}>
                            <PhotoThumb>
                                <img
                                    src={photo.replacementFile
                                        ? URL.createObjectURL(photo.replacementFile)
                                        : photo.url}
                                    alt=""
                                />
                                {photo.replacementFile && (
                                    <ReplacementOverlay>
                                        <span>{isFr ? 'Remplacée' : 'Replaced'}</span>
                                    </ReplacementOverlay>
                                )}
                            </PhotoThumb>
                            <PhotoDetails>
                                <FormGroup style={{ marginBottom: 8 }}>
                                    <label>{isFr ? 'Crédit photographe' : 'Photographer credit'}</label>
                                    <input
                                        value={photo.credit}
                                        onChange={e => updateExistingCredit(realIndex, e.target.value)}
                                        placeholder={isFr ? 'Ex: © Jean Dupont' : 'Ex: © John Smith'}
                                        style={{ fontSize: 13 }}
                                    />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 8 }}>
                                    <label>{isFr ? 'Type de photo' : 'Photo type'}</label>
                                    <CategorySelect
                                        value={photo.category || ''}
                                        onChange={e => updateExistingCategory(realIndex, e.target.value || '')}
                                    >
                                        <option value="">{isFr ? '— Non défini —' : '— Not set —'}</option>
                                        <option value="packshot">Packshot</option>
                                        <option value="context">{isFr ? 'Prise de vue en contexte' : 'Context shot'}</option>
                                    </CategorySelect>
                                </FormGroup>
                                <PhotoActionRow>
                                    {photo.replacementFile ? (
                                        <SmallBtn onClick={() => cancelReplacement(realIndex)}>
                                            {isFr ? 'Annuler le remplacement' : 'Cancel replacement'}
                                        </SmallBtn>
                                    ) : (
                                        <SmallBtn onClick={() => handleReplacePhoto(realIndex)}>
                                            {isFr ? 'Remplacer' : 'Replace'}
                                        </SmallBtn>
                                    )}
                                    <SmallBtn variant="danger" onClick={() => deleteExistingPhoto(realIndex)}>
                                        {isFr ? 'Supprimer' : 'Delete'}
                                    </SmallBtn>
                                </PhotoActionRow>
                            </PhotoDetails>
                        </ExistingPhotoCard>
                    );
                })}

                {/* Deleted photos */}
                {deletedExisting.length > 0 && (
                    <>
                        <p style={{ fontSize: 13, color: '#999', marginTop: 16, marginBottom: 8, fontStyle: 'italic' }}>
                            {isFr
                                ? `${deletedExisting.length} photo(s) supprimée(s)`
                                : `${deletedExisting.length} deleted photo(s)`}
                        </p>
                        {deletedExisting.map((photo) => {
                            const realIndex = existingPhotos.indexOf(photo);
                            return (
                                <ExistingPhotoCard key={photo.url + realIndex} deleted>
                                    <PhotoThumb>
                                        <img src={photo.url} alt="" />
                                    </PhotoThumb>
                                    <PhotoDetails style={{ justifyContent: 'center' }}>
                                        <SmallBtn variant="restore" onClick={() => restoreExistingPhoto(realIndex)}>
                                            {isFr ? 'Restaurer' : 'Restore'}
                                        </SmallBtn>
                                    </PhotoDetails>
                                </ExistingPhotoCard>
                            );
                        })}
                    </>
                )}

                {/* Hidden input for photo replacement */}
                <input
                    ref={replaceInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={onReplacementSelected}
                />

                {/* Add new photos */}
                <div style={{ marginTop: existingPhotos.length > 0 ? 20 : 0 }}>
                    <DropZone
                        isDragOver={dragOver}
                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleNewPhotoDrop}
                        onClick={() => newPhotoInputRef.current?.click()}
                    >
                        <input
                            ref={newPhotoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleNewPhotoDrop}
                        />
                        {isFr
                            ? 'Glissez vos photos ici ou cliquez pour sélectionner'
                            : 'Drag photos here or click to select'}
                        <br />
                        <span style={{ fontSize: 12, color: '#aaa' }}>JPEG, PNG, WebP</span>
                    </DropZone>

                    {newPhotos.map((np, i) => (
                        <NewPhotoCard key={`new-${i}`}>
                            <NewPhotoThumb>
                                <img src={URL.createObjectURL(np.file)} alt="" />
                            </NewPhotoThumb>
                            <PhotoDetails>
                                <FormGroup style={{ marginBottom: 8 }}>
                                    <label>{isFr ? 'Type de photo' : 'Photo type'}</label>
                                    <CategorySelect
                                        value={np.category}
                                        onChange={e => updateNewCategory(i, e.target.value)}
                                    >
                                        <option value="packshot">Packshot</option>
                                        <option value="context">{isFr ? 'Prise de vue en contexte' : 'Context shot'}</option>
                                    </CategorySelect>
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Crédit photographe' : 'Photographer credit'}</label>
                                    <input
                                        value={np.credit}
                                        onChange={e => updateNewCredit(i, e.target.value)}
                                        placeholder={isFr ? 'Ex: © Jean Dupont' : 'Ex: © John Smith'}
                                        style={{ fontSize: 13 }}
                                    />
                                </FormGroup>
                            </PhotoDetails>
                            <RemoveBtn onClick={() => removeNewPhoto(i)}>&times;</RemoveBtn>
                        </NewPhotoCard>
                    ))}
                </div>
            </SectionCard>

            {/* ── Section 3: Contact ── */}
            <SectionCard>
                <SectionTitle>
                    {isFr ? 'Contact' : 'Contact'}
                </SectionTitle>

                <FieldRow>
                    <FormGroup className="required">
                        <label>{isFr ? 'Prénom' : 'First name'}</label>
                        <input value={contactPrenom} onChange={e => setContactPrenom(e.target.value)} />
                    </FormGroup>
                    <FormGroup className="required">
                        <label>{isFr ? 'Nom' : 'Last name'}</label>
                        <input value={contactNom} onChange={e => setContactNom(e.target.value)} />
                    </FormGroup>
                </FieldRow>

                <FieldRow>
                    <FormGroup className="required">
                        <label>{isFr ? 'Email' : 'Email'}</label>
                        <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
                    </FormGroup>
                    <FormGroup className="required">
                        <label>{isFr ? 'Téléphone' : 'Phone'}</label>
                        <input type="tel" value={contactTelephone} onChange={e => setContactTelephone(e.target.value)} />
                    </FormGroup>
                </FieldRow>
            </SectionCard>

            {/* ── Submit ── */}
            <SubmitBtn onClick={handleSubmit} disabled={submitting}>
                {submitting
                    ? (isFr ? 'Envoi en cours...' : 'Submitting...')
                    : piece?.submitted_at
                        ? (isFr ? 'Mettre à jour' : 'Update')
                        : (isFr ? 'Envoyer' : 'Submit')}
            </SubmitBtn>
        </PageContainer>
    );
};

export default FD100ExpoConfirmationForm;
